<script lang="ts">
	import { BadgeLatestSeats, BadgePromoDetails } from '$lib/components/atoms/Badge';
	import { Icon } from '$lib/components/atoms/Icon';
	import { Price } from '$lib/components/atoms/Price';
	import { lessionModeText, lessonModeIcon } from '$lib/utils/course-utils';
	import { dateFullMonthFormat, isValidDate } from '$lib/utils/formatter';
	import { omit } from '$lib/utils/object';
  import { SessionLightModel, SessionModel } from '@24hbs/utils';

  export let id: string;
  export let value: string;
  export let name: string;
  export let checked: boolean = false;
  export let disabled: boolean = false;
  export let session: SessionModel | SessionLightModel = new SessionModel();
  export let hasVatFree: boolean = false;
  export let showLessionMode: boolean = false;

  let input: HTMLInputElement;
  let lessonModeIconName: string | null = null;

  const onClick = () => {
    document
      .querySelectorAll<HTMLInputElement>(`input[name=${input.name}]:checked`)
      .forEach((checkedInput) => {
        if (checkedInput !== input) {
          checkedInput.checked = false;
          checkedInput.dispatchEvent(new Event('change'));
        }
      });
  };

  const onChange = () => {
    checked = input.checked;
  };

  const attributes = omit($$restProps, 'class');

  $: lessonModeIconName = lessonModeIcon(session.lessionMode);
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<label
  for={id}
  class="schedule-radio-button"
  class:checked
  class:disabled
  on:click={onClick}
>
  <input
    {id}
    type="radio"
    {name}
    {value}
    {checked}
    class="sr-only"
    on:change={onChange}
    bind:this={input}
    {...attributes}
  />
  <div class="schedule-info">
    {#if isValidDate(session.startDate)}
      {#if session.lessonsAvailableImmediately}
      <div class="schedule-dates">
        <div class="schedule-date">
          <span class="date-label">Lezioni disponibili da subito</span>
        </div>
      </div>
      {:else} 
        <div class="schedule-dates">
          <div class="schedule-date">
            <span class="date-label">Dal</span>
            <span class="date">{dateFullMonthFormat(session.startDate)}</span>
          </div>
          <div class="schedule-date">
            <span class="date-label">Al</span>
            <span class="date">{dateFullMonthFormat(session.endDate)}</span>
          </div>
        </div>
      {/if}
    {:else}
      <div class="schedule-dates">
        <div class="schedule-date">
          <span class="date-label">On Demand</span>
        </div>
      </div>
    {/if}
    {#if session.lessionMode && showLessionMode}
      <div class="course-data-item">
        <span class="data-icon">
          <Icon icon={lessonModeIconName} />
        </span>
        <span class="data-text">
          {lessionModeText(session.lessionMode).toUpperCase()}
        </span>
      </div>
    {/if}
  </div>

  <div class="schedule-info-price">
    {#if session.lessionMode && showLessionMode}
      <div class="course-data-item-mobile">
        <span class="data-icon">
          <Icon icon={lessonModeIconName} />
        </span>
        <span class="mr-4">
          {lessionModeText(session.lessionMode).toUpperCase()}
        </span>
      </div>
    {/if}
    <div class="schedule-price">
      <Price
        {session}
        {hasVatFree}
      />
      <div class="hidden md:block mr-4">
        {#if session.lastSeats && !session.soldOut}
          <BadgeLatestSeats onSchedule session={session} />
        {:else if session.promoDetailLabel}
          <BadgePromoDetails onSchedule session={session} />
        {/if}
      </div>
    </div>
  </div>
  <div class="md:hidden">
    {#if session.lastSeats && !session.soldOut}
      <BadgeLatestSeats onSchedule session={session} />
    {:else if session.promoDetailLabel}
      <BadgePromoDetails onSchedule session={session} />
    {/if}
  </div>
  {#if checked}
    <span class="checkmark">
      <Icon icon="check" />
    </span>
  {/if}
</label>

<style type="text/scss" lang="scss" scoped>
  .schedule-radio-button {
    @apply relative block;
    @apply rounded-lg border border-gray-300;
    @apply bg-white shadow-sm;
    @apply px-4 py-4 md:px-6;
    @apply mr-4 md:mr-0; /** checkmark space */
    @apply cursor-pointer;
    @apply transition-colors duration-100 ease-in;
    @apply md:flex sm:justify-between;

    &:not(.disabled):hover {
      @apply border-gray-600 border-opacity-75;
    }

    &:focus-within {
      @apply ring-1 ring-offset-2 ring-brand;
    }

    &.checked {
      @apply border-gray-600;
    }

    &.disabled {
      @apply pointer-events-none cursor-default;
    }
  }

  .checkmark {
    @apply absolute right-0 top-0;
    @apply -m-4 p-0.5;
    @apply w-8 h-8;
    @apply rounded-full;
    @apply bg-gray-900;
    @apply border-3 border-white;
    @apply text-white;
    @apply flex items-center justify-center;
  }

  .schedule-dates {
    @apply flex items-center;
    @apply divide-x divide-gray-300;
  }
  .schedule-date {
    @apply flex flex-col items-start;
    @apply mb-4;
    @apply md:pb-4 md:mb-0;

    &:first-child {
      @apply pr-6;
    }
    &:last-child {
      @apply pl-6;
    }
  }
  .date-label {
    @apply font-semibold uppercase tracking-wider;
    @apply leading-tight;
    @apply text-gray-600;
  }
  .date {
    @apply font-semibold uppercase tracking-wider;
    @apply leading-tight;
    @apply text-gray-900;
  }

  .schedule-price {
    @apply ml-auto flex items-start lg:items-end;
    @apply flex-col;
    @apply md:w-auto;
    min-width: fit-content;
  }
  :global(.schedule-price > *) {
    @apply flex-grow md:flex-grow-0;
  }

  .last-seats {
    @apply bg-petrol py-2 px-6;
    @apply w-full sm:max-w-xs md:w-auto;
    @apply mb-4 md:mb-0 md:mr-2;
    @apply text-white text-xs font-semibold uppercase tracking-wide;
    @apply whitespace-nowrap;
    @apply text-center;
    @apply rounded;
  }

  .course-data-item {
    @apply hidden md:flex;
  }
  .course-data-item-mobile {
    @apply flex items-center md:hidden;
  }
  .data-icon {
    @apply inline-flex flex-shrink-0 mr-2;
  }
  .data-text {
    @apply flex-shrink-0;
  }
  .schedule-info-price {
    @apply flex items-center flex-row justify-between;
  }
</style>
